import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAllDistributionsProcessBySellerId } from "@/Api/distributions";
import { getDistributionProcessFiles } from "@/Api/distributionsProcess";
import { DISTRIBUTION_PROCESS_STATUS } from "@/constants/distributionStatus";
import { useAuth } from "@/hooks/auth";
import {
  ContestGroupType,
  DistributionFileType,
  DistributionItemType,
  RawDistributionProcessType,
} from "../types";

/**
 * Normaliza o payload da API em itens achatados, já com o contexto do concurso.
 *
 * Cópia deliberada da transformação da tela antiga (SellersPending): as duas
 * telas são independentes de propósito e não compartilham código.
 */
export const normalizeDistributions = (
  data: RawDistributionProcessType[]
): DistributionItemType[] =>
  data
    .filter((item) => Boolean(item?.contest_product_classification?.contest))
    .map((item) => {
      const cpc = item.contest_product_classification;

      return {
        id: item.id,
        uuid: item.uuid,
        name: cpc.product_name,
        classification: cpc.product_classification?.name ?? "",
        quotas: item.quota_quantity,
        distributionTypeId: item.distribution_type_id,
        statusId: item.distribution_process_status_id,
        statusName: item.distribution_process_status?.name ?? "",
        sellerName: item.seller?.user?.name ?? "",
        sellerEmail: item.seller?.user?.email ?? "",
        createdByName: item.created_by?.name ?? "",
        createdAt: item.created_at,
        updatedByName: item.updated_by?.name ?? null,
        updatedAt: item.processed_at || item.updated_at,
        contestId: String(cpc.contest_id),
        contestName: cpc.contest?.name ?? null,
        lotteryId: cpc.lottery_id,
        lotteryName: cpc.lottery?.name ?? "",
        lotteryColor: cpc.lottery?.color || "#666",
      };
    });

export const useDistributionData = () => {
  const [state, setState] = useState<"loading" | "production" | "error">("loading");
  const [rawData, setRawData] = useState<RawDistributionProcessType[]>([]);
  const [filesMap, setFilesMap] = useState<Record<number, DistributionFileType[]>>({});
  const [loadingFiles, setLoadingFiles] = useState<Record<string, boolean>>({});

  const loadedContestsRef = useRef<Set<string>>(new Set());
  const sellerId = useAuth()?.user?.seller?.id;

  const items = useMemo(() => normalizeDistributions(rawData), [rawData]);

  const totals = useMemo(() => {
    const pendingItems = items.filter(
      (item) => item.statusId === DISTRIBUTION_PROCESS_STATUS.PENDING
    );

    return {
      counts: {
        all: items.length,
        pending: pendingItems.length,
        accepted: items.filter(
          (item) => item.statusId === DISTRIBUTION_PROCESS_STATUS.ACCEPTED
        ).length,
        rejected: items.filter(
          (item) => item.statusId === DISTRIBUTION_PROCESS_STATUS.REJECTED
        ).length,
      },
      pendingQuotas: pendingItems.reduce(
        (total, item) => total + (item.quotas ?? 0),
        0
      ),
    };
  }, [items]);

  const refetch = useCallback(async () => {
    if (!sellerId) {
      setState("production");
      return;
    }

    try {
      setState("loading");
      loadedContestsRef.current = new Set();

      const { data } = await getAllDistributionsProcessBySellerId(String(sellerId));

      setRawData(data?.response?.data || []);
      setState("production");
    } catch (error) {
      console.error(error);
      setState("error");
    }
  }, [sellerId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  /** Fotos são buscadas só quando o concurso é expandido, e uma vez por carga. */
  const loadFilesForContest = useCallback(async (contest: ContestGroupType) => {
    const ids = contest.items.map((item) => item.id);

    if (ids.length === 0 || loadedContestsRef.current.has(contest.id)) return;

    loadedContestsRef.current.add(contest.id);
    setLoadingFiles((previous) => ({ ...previous, [contest.id]: true }));

    try {
      const response = await getDistributionProcessFiles(ids);
      const payload = response?.data?.response as
        | Record<string, { id: number; file_name: string; url: string }[]>
        | undefined;

      if (!response?.data?.status || !payload) return;

      const nextFiles: Record<number, DistributionFileType[]> = {};

      Object.entries(payload).forEach(([processId, files]) => {
        nextFiles[Number(processId)] = files.map((file) => ({
          id: file.id,
          name: null,
          file: "",
          file_name: file.file_name,
          url: file.url,
          created_at: "",
        }));
      });

      setFilesMap((previous) => ({ ...previous, ...nextFiles }));
    } catch (error) {
      console.error("Erro ao carregar arquivos:", error);
    } finally {
      setLoadingFiles((previous) => ({ ...previous, [contest.id]: false }));
    }
  }, []);

  return {
    state,
    items,
    totals,
    refetch,
    filesMap,
    loadingFiles,
    loadFilesForContest,
  };
};
